import type { ComponentType } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from '@/context/AuthContext';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { Layout } from '@/components/Layout';
import { APP_ROUTES, type AppRoute, type RouteId } from '@/config/routes';
import {
  Login,
  Dashboard,
  Stats,
  Nodes,
  Domains,
  OriginGroups,
  Certificates,
  Nginx,
  Tasks,
  Users,
  Audit,
  NotFound,
} from '@/pages';

/**
 * Page component for each route id. Typed as an exhaustive record, so adding
 * a route to config/routes.ts without a page is a compile error.
 */
const PAGE_COMPONENTS: Record<RouteId, ComponentType> = {
  dashboard: Dashboard,
  stats: Stats,
  nodes: Nodes,
  domains: Domains,
  'origin-groups': OriginGroups,
  certificates: Certificates,
  nginx: Nginx,
  tasks: Tasks,
  users: Users,
  audit: Audit,
};

/**
 * Render one configured route; permission-guarded routes get a nested
 * ProtectedRoute with the route's `requires` action.
 */
function renderRoute(route: AppRoute) {
  const Page = PAGE_COMPONENTS[route.id];
  const element = <Page />;
  const routeElement =
    route.path === '/' ? (
      <Route key={route.id} index element={element} />
    ) : (
      <Route key={route.id} path={route.path.slice(1)} element={element} />
    );

  if (!route.requires) {
    return routeElement;
  }

  return (
    <Route key={route.id} element={<ProtectedRoute requires={route.requires} />}>
      {routeElement}
    </Route>
  );
}

/**
 * Main App component with routing configuration
 *
 * Routes come from config/routes.ts (also used by the sidebar and header):
 * - /login - Public login page (redirects to dashboard if already authenticated)
 * - all other routes require authentication; routes with `requires` also
 *   require that permission (e.g. /users -> users:manage, admin only)
 *
 * If not authenticated, users are redirected to /login with their attempted
 * path preserved for redirect after successful login.
 */
function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />

          {/* Protected routes - require authentication */}
          <Route element={<ProtectedRoute />}>
            <Route element={<Layout />}>
              {APP_ROUTES.map(renderRoute)}

              {/* 404 within authenticated area */}
              <Route path="*" element={<NotFound />} />
            </Route>
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
